import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Text, Loader, Group } from '@mantine/core';
import { AlertCircle, FileText, Calendar, User, Clock, Globe } from 'lucide-react';
import { notesApi } from '../api/notesApi';
import { PublicNote } from '../types/notes';
import { NoteRichTextEditor } from './notes/NoteRichTextEditor';
import { NoteEditorProvider } from '../contexts/NoteEditorContext';
import './PublicNoteView.css';

export const PublicNoteView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [note, setNote] = useState<PublicNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNote = async () => {
      if (!token) {
        console.error('No token provided');
        setError('Invalid note token');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const publicNote = await notesApi.getPublicNote(token);
        setNote(publicNote);
      } catch (err) {
        console.error('Failed to fetch public note:', err);
        setError('Note not found or no longer available');
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [token]);

  if (loading) {
    return (
      <div className="PublicNote-wrapper">
        <div className="PublicNote-loading-container">
          <Group gap="md">
            <Loader size="md" />
            <Text size="lg" style={{ color: 'var(--app-text-muted)' }}>
              Loading note...
            </Text>
          </Group>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="PublicNote-wrapper">
        <div className="PublicNote-error-container">
          <div className="PublicNote-error-message">
            <AlertCircle size={48} />
            <h2>Note Not Found</h2>
            <p>
              {error || 'The requested note could not be found. It may have been deleted or is no longer shared publicly.'}
            </p>
            <p className="PublicNote-error-cta">
              Like socks in a dryer, this note disappeared. Grab a fresh one at{' '}
              <a
                href="https://symphonytodo.com"
                className="PublicNote-error-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                symphonytodo.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Generate dynamic color for the note based on content
  const noteColor = note.color || '#3b82f6';

  return (
    <div 
      className="PublicNote-wrapper"
      style={{ '--public-note-color': noteColor } as React.CSSProperties}
    >
      <div className="PublicNote-scrollable-container">
        {/* Header */}
        <header className="PublicNote-header">
          <div className="PublicNote-header-content">
            <div className="PublicNote-info">
              {note.title && (
                <h1 className="PublicNote-title">{note.title}</h1>
              )}
              
              <div className="PublicNote-meta">
                <span>
                  <User size={16} />
                  By {note.createdBy ? note.createdBy.split('@')[0] : 'Unknown'}
                </span>
                <span>
                  <Calendar size={16} />
                  {new Date(note.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                <span>
                  <Clock size={16} />
                  {(() => {
                    const updatedDate = new Date(note.updatedAt);
                    const now = new Date();
                    const diffInDays = Math.floor((now.getTime() - updatedDate.getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (diffInDays === 0) return 'Today';
                    if (diffInDays === 1) return 'Yesterday';
                    if (diffInDays < 7) return `${diffInDays} days ago`;
                    return updatedDate.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  })()}
                </span>
                <span>
                  <FileText size={16} />
                  {(() => {
                    const text = note.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                    const words = text ? text.split(' ').length : 0;
                    const chars = text.length;
                    if (words > 0) return `${words} words`;
                    return `${chars} chars`;
                  })()}
                </span>
              </div>

              {note.tags && note.tags.trim() && (
                <div className="PublicNote-tags">
                  {note.tags.split(',').filter((tag: string) => tag.trim()).map((tag: string, index: number) => {
                    // Generate a hue based on the tag content for consistent coloring
                    const tagHue = tag.trim().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;
                    return (
                      <span 
                        key={index}
                        className="PublicNote-tag"
                        style={{ '--tag-hue': tagHue } as React.CSSProperties}
                      >
                        {tag.trim()}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="PublicNote-public-badge">
              <Globe size={12} />
              Public
            </div>
          </div>
        </header>

        <div className="PublicNote-content">
          
          <NoteEditorProvider>
            <div className="PublicNote-rich-content">
              <NoteRichTextEditor
                content={note.content}
                onChange={() => {}} // Read-only, no changes allowed
                placeholder=""
                isEditing={false} // Read-only mode
                autoFocus={false}
                showToolbar={false}
              />
            </div>
          </NoteEditorProvider>
        </div>

        <footer className="PublicNote-footer">
          <p className="PublicNote-footer-text">
            This note is shared publicly. Create your own notes at{' '}
            <a href={window.location.origin} className="PublicNote-footer-link">
              SymphonyTodo
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PublicNoteView;